import {Box, Button, Chip, Divider, Paper, TextField, Typography} from "@mui/material";
import {saveCompany} from "../../../services/CompanyService";
import {Autocomplete} from "@mui/lab";
import {useMessageContext} from "../../../contexts/MessageContext";
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const CompanyInfoPage = ({company, setCompany, setLoader, setRefresh, setDeleteCompanyDialog}) => {

  const {notificationHandler} = useMessageContext();

  const updateName = (value) => {
    setCompany(prv => {
      return {...prv, name : value}
    })
  }

  const updateInfo = (value) => {
    setCompany(prv => {
      return {...prv, info : value}
    })
  }

  const updateIndustry = (value) => {
    setCompany(prv => {
      return {...prv, industry : value}
    })
  }

  const updateCompanyDifferentiators = (value) => {
    setCompany(prv => {
      return {...prv, companyDifferentiators : value}
    })
  }

  const updateCompanyPersonasProduct = (value) => {
    setCompany(prv => {
      return {...prv, personasProduct : value}
    })
  }

  const updateCompanyPersonasSeller = (value) => {
    setCompany(prv => {
      return {...prv, personasSeller : value}
    })
  }

  const updateCompanyCompanyStandards = (value) => {
    setCompany(prv => {
      return {...prv, companyStandards : value}
    })
  }

  const updateCompanySolution = (value) => {
    setCompany(prv => {
      return {...prv, companySolution : value}
    })
  }

  const updateCompanyCategory = (value) => {
    setCompany(prv => {
      return {...prv, companyCategory : value}
    })
  }

  const updateValueProposition = (value) => {
    setCompany(prv => {
      return {...prv, valueProposition : value}
    })
  }

  const updateLanguages = (value) => {
    setCompany(prv => {
      return {...prv, languages : value}
    })
  }

  const updateIcp = (value) => {
    setCompany(prv => {
      return {...prv, icp : value}
    })
  }

  const updateCompanyType = (value) => {
    setCompany(prv => {
      return {...prv, companyType : value}
    })
  }

  const updateTargetCountries = (value) => {
    setCompany(prv => {
      return {...prv, targetCountries : value}
    })
  }

  return (
    <Paper
      component="form"
      onSubmit={(event => {
        event.preventDefault();
        setLoader(true);
        saveCompany(company)
            .then(() => setRefresh(prv => !prv))
            .then(() => notificationHandler.success('Company Saved'))
            .finally(() => setLoader(false))
      })}
      sx={{ mt: 3, p: 3 }}
      elevation={1}
    >
      {/* Basic Information */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Basic Information
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
            disabled
            size="small"
            sx={{ width: "100px" }}
            id="id"
            name="id"
            label="Id"
            type="number"
            variant="outlined"
            value={company.id}
        />
        <TextField
            required
            size="small"
            sx={{ flex: 1 }}
            id="name"
            name="name"
            label="Company Name"
            type="text"
            variant="outlined"
            onChange={(e) => updateName(e.target.value)}
            value={company.name}
        />
        <TextField
            size="small"
            sx={{ flex: 1 }}
            id="industry"
            name="industry"
            label="Company Industry"
            type="text"
            variant="outlined"
            onChange={(e) => updateIndustry(e.target.value)}
            value={company.industry}
        />
      </Box>

      <TextField
          required
          multiline
          fullWidth
          size="small"
          minRows={3}
          id="info"
          name="info"
          label="Company Information"
          type="text"
          variant="outlined"
          onChange={(e) => updateInfo(e.target.value)}
          value={company.info}
          sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 3 }} />

      {/* Company Details */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Company Details
      </Typography>

      <TextField
          multiline
          fullWidth
          size="small"
          minRows={3}
          id="companySolution"
          name="Company Solution"
          label="Company Solution"
          type="text"
          variant="outlined"
          onChange={(e) => updateCompanySolution(e.target.value)}
          value={company.companySolution}
          sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
            size="small"
            sx={{ flex: 1 }}
            id="companyVertical"
            name="Company Vertical"
            label="Company Vertical"
            type="text"
            variant="outlined"
            onChange={(e) => updateCompanyCategory(e.target.value)}
            value={company.companyCategory}
        />
        <TextField
            size="small"
            sx={{ flex: 1 }}
            id="CompanyValueProposition"
            name="Company Value Proposition"
            label="Company Value Proposition"
            type="text"
            variant="outlined"
            onChange={(e) => updateValueProposition(e.target.value)}
            value={company.valueProposition}
        />
      </Box>

      <TextField
          multiline
          fullWidth
          size="small"
          minRows={2}
          maxRows={4}
          id="icp"
          name="icp"
          label="ICP (Ideal Customer Profile)"
          type="text"
          variant="outlined"
          placeholder="Describe your ideal customer profile"
          onChange={(e) => updateIcp(e.target.value)}
          value={company.icp || ''}
          sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 3 }} />

      {/* Personas & Differentiators */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Personas & Differentiators
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Autocomplete
            multiple
            size="small"
            id="persona-users"
            sx={{ flex: 1 }}
            options={[]}
            freeSolo
            value={company.personasProduct || []}
            onChange={(event, newValue) => {
              updateCompanyPersonasProduct(newValue);
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        size="small"
                        sx={{ height: 'auto', '& .MuiChip-label': { display: 'block', whiteSpace: 'normal' } }}
                        variant="filled"
                        label={option}
                        {...tagProps}
                      />
                    );
                })
            }
            renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Persona Users" placeholder="Add persona..." />
            )}
        />
        <Autocomplete
            multiple
            size="small"
            id="persona-buyer"
            sx={{ flex: 1 }}
            options={[]}
            freeSolo
            value={company.personasSeller || []}
            onChange={(event, newValue) => {
              updateCompanyPersonasSeller(newValue);
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        size="small"
                        sx={{ height: 'auto', '& .MuiChip-label': { display: 'block', whiteSpace: 'normal' } }}
                        variant="filled"
                        label={option}
                        {...tagProps}
                      />
                    );
                })
            }
            renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Persona Buyer" placeholder="Add persona..." />
            )}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Autocomplete
            multiple
            size="small"
            id="differentiators"
            sx={{ flex: 1 }}
            options={[]}
            freeSolo
            value={company.companyDifferentiators || []}
            onChange={(event, newValue) => {
              updateCompanyDifferentiators(newValue);
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        size="small"
                        sx={{ height: 'auto', '& .MuiChip-label': { display: 'block', whiteSpace: 'normal' } }}
                        variant="filled"
                        label={option}
                        {...tagProps}
                      />
                    );
                })
            }
            renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Company Differentiators" placeholder="Add differentiator..." />
            )}
        />
        <Autocomplete
            multiple
            size="small"
            id="standards"
            sx={{ flex: 1 }}
            options={[]}
            freeSolo
            value={company.companyStandards || []}
            onChange={(event, newValue) => {
              updateCompanyCompanyStandards(newValue);
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        size="small"
                        sx={{ height: 'auto', '& .MuiChip-label': { display: 'block', whiteSpace: 'normal' } }}
                        variant="filled"
                        label={option}
                        {...tagProps}
                      />
                    );
                })
            }
            renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Customer Standards" placeholder="Add standard..." />
            )}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Additional Settings */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Additional Settings
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Autocomplete
            multiple
            size="small"
            id="languages"
            sx={{ flex: 1 }}
            options={[]}
            freeSolo
            value={company.languages || []}
            onChange={(event, newValue) => {
              updateLanguages(newValue);
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={key} size="small" variant="filled" label={option} {...tagProps} />;
                })
            }
            renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Languages" placeholder="Add language..." />
            )}
        />
        <Autocomplete
            multiple
            size="small"
            id="company-type"
            sx={{ flex: 1 }}
            options={[]}
            freeSolo
            value={company.companyType || []}
            onChange={(event, newValue) => {
              updateCompanyType(newValue);
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={key} size="small" variant="filled" label={option} {...tagProps} />;
                })
            }
            renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Company Type" placeholder="Add type..." />
            )}
        />
        <Autocomplete
            multiple
            size="small"
            id="target-countries"
            sx={{ flex: 1 }}
            options={[]}
            freeSolo
            value={company.targetCountries || []}
            onChange={(event, newValue) => {
              updateTargetCountries(newValue);
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={key} size="small" variant="filled" label={option} {...tagProps} />;
                })
            }
            renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Target Countries" placeholder="Add country..." />
            )}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button
          onClick={() => setDeleteCompanyDialog(true)}
          variant="outlined"
          color="error"
          startIcon={<DeleteOutlineIcon />}
        >
          Delete Company
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
        >
          Save Changes
        </Button>
      </Box>
    </Paper>
  );
}

export default CompanyInfoPage;
